import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useOrgs } from "@/lib/orgs";
import { StatusPill, type DeploymentStatus } from "@/components/StatusPill";
import { EnvBadge, type Env } from "@/components/EnvBadge";
import { CommitRef } from "@/components/CommitRef";
import { RelativeTime } from "@/components/RelativeTime";

interface ProjectRow {
  id: string;
  slug: string;
  framework: "vite" | "next";
  latest: {
    id: string;
    status: DeploymentStatus;
    env: Env;
    branch: string;
    commitSha: string;
    commitMessage?: string;
    readyAt?: string | null;
    createdAt: string;
  } | null;
}

export function Dashboard() {
  const { org: orgParam } = useParams<{ org?: string }>();
  const orgs = useOrgs();
  const orgSlug = orgParam ?? orgs.data?.[0]?.slug;

  const projects = useQuery({
    enabled: !!orgSlug,
    queryKey: ["projects", orgSlug],
    queryFn: async () => {
      const r = await api.get<{ projects: ProjectRow[] }>(`/orgs/${orgSlug}/projects`);
      return r.projects;
    },
  });

  return (
    <div className="container max-w-screen-xl py-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Projects</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {orgSlug ? <>in <span className="font-mono">{orgSlug}</span></> : null}
          </p>
        </div>
        <Button asChild variant="primary"><Link to="/orgs/new/import"><Plus className="h-3.5 w-3.5" /> Import project</Link></Button>
      </div>

      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold">{projects.data?.length ?? 0} projects</h3>
        </div>
        {projects.isLoading && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {projects.data?.length === 0 && (
          <div className="px-4 py-12 text-center">
            <div className="text-sm text-muted-foreground">No projects yet.</div>
            <Button asChild variant="primary" size="sm" className="mt-3">
              <Link to="/orgs/new/import"><Plus className="h-3.5 w-3.5" /> Import from GitHub</Link>
            </Button>
          </div>
        )}
        {projects.data?.map((p) => (
          <Link
            key={p.id}
            to={`/orgs/${orgSlug}/projects/${p.slug}`}
            className="of-row flex-wrap hover:bg-subtle"
          >
            <div className="min-w-0 w-full sm:w-auto sm:flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{p.slug}</span>
                <span className="text-2xs text-subtle-foreground font-mono uppercase tracking-wider">
                  {p.framework}
                </span>
              </div>
              {p.latest ? (
                <div className="mt-1 flex items-center gap-2">
                  <CommitRef branch={p.latest.branch} sha={p.latest.commitSha} message={p.latest.commitMessage ?? null} />
                </div>
              ) : (
                <div className="mt-1 text-xs text-subtle-foreground italic">No deployments yet</div>
              )}
            </div>
            {p.latest && (
              <>
                <EnvBadge env={p.latest.env} />
                <StatusPill status={p.latest.status} />
                <RelativeTime value={p.latest.readyAt ?? p.latest.createdAt} />
                <ExternalLink className="h-3.5 w-3.5 text-subtle-foreground" />
              </>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
